// Historia 179 — el ESTADO del campo Workflow de una OF: lectura (C1), opciones (C10), elección pendiente,
// confirmación, guardado (C2) y rechazos. Lo usa el único componente del campo,
// `FabricationOrderWorkflowField`, que montan las DOS puertas de la operación (RN16, EM-5): el bloque General
// de la ficha y el diálogo de la acción de fila de la ventana antigua.
//
// ⚠️ LA REGLA NO SE REIMPLEMENTA AQUÍ (RN29, INV8): si se puede tocar, por qué no y si se puede quitar lo dice
// Sun en C1, con la misma regla con la que valida C2. C1, C2 y C10 viven solo en `detailApi.js`.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { workflowOriginTextOf } from '../../../generic/utilities/workflowOrigin'
import { fetchApprovedWorkflows, fetchWorkflowAssignment, saveWorkflowAssignment } from './detailApi'
import { CONFIRMATION_TEXTS, currentOptionOf, isRemoval, plannedKindOf, reasonKeyOf, rejectionOf, workflowFieldOptionsOf } from './workflowAssignment'

/**
 * @param {Object} params
 * @param {number} params.fabricationOrderId La OF.
 * @param {function(Object)} params.onAssigned Tras un C2 correcto, con su respuesta: cada puerta decide qué
 *   recarga (la ficha, sus bloques; la ventana antigua, la página del listado).
 * @param {function(Object)} params.showAlert El `AlertUI` donde se avisan los rechazos (B3).
 */
const useFabricationOrderWorkflow = ({ fabricationOrderId, onAssigned, showAlert }) => {
  const { t_ } = useSmartTranslation()
  const [assignment, setAssignment] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [approvedWorkflows, setApprovedWorkflows] = useState([])
  // La opción que el usuario ha elegido y todavía no ha confirmado. Mientras hay una, el campo la enseña
  // y la confirmación está abierta; sin ella, el campo enseña el valor actual.
  const [selection, setSelection] = useState(null)
  const [saving, setSaving] = useState(false)
  // E4 con el campo todavía editable: la confirmación sigue abierta y dice por qué (S110).
  const [concurrentNotice, setConcurrentNotice] = useState(false)
  // ⚠️ Se vuelve a poner a `true` al MONTAR, no solo al declararla: `React.StrictMode` (el de la app) monta,
  // desmonta y remonta los efectos en desarrollo, y una guarda que solo se apaga dejaba C1 descartado para
  // siempre (campo vacío y con candado, visto en la app con la OF 8).
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  // C1. `Promise.resolve().then(...)`, como los catálogos de General: una lectura que revienta de forma
  // síncrona sale como promesa rechazada, nunca como excepción en el render.
  const readAssignment = useCallback(() => Promise.resolve()
    .then(() => fetchWorkflowAssignment(fabricationOrderId)), [fabricationOrderId])

  useEffect(() => {
    readAssignment()
      .then((data) => {
        if (!alive.current) return
        setAssignment(data)
        setLoaded(true)
      })
      // S121 (RN28, VC-18): si C1 se rechaza —el 403 de autoridades, sin `code`, o cualquier otro— se avisa
      // con la clave de su rechazo y el campo se queda sin ofrecer nada: sin C1 no hay a qué decir que sí.
      .catch((error) => {
        if (!alive.current) return
        const rejection = rejectionOf(error)
        showAlert({ severity: 'warning', title: t_(rejection.key, rejection.params), message: '' })
      })
    // `showAlert` y `t_` son estables (un `AlertUI` y la `t` de i18next): C1 se pide una vez por OF, que es
    // lo que dice S101.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readAssignment])

  // Las opciones (C10) se piden solo si hay algo que elegir: con el campo bloqueado no se ofrece nada.
  const partReferenceId = loaded && assignment.editable ? assignment.partReferenceId : ''
  useEffect(() => {
    if (!partReferenceId) return undefined
    let current = true
    Promise.resolve()
      .then(() => fetchApprovedWorkflows(partReferenceId))
      .then((list) => { if (current) setApprovedWorkflows(list) })
      .catch(() => { if (current) setApprovedWorkflows([]) })
    return () => { current = false }
  }, [partReferenceId])

  const editable = loaded && assignment.editable
  const options = loaded ? workflowFieldOptionsOf(assignment, approvedWorkflows, t_) : []
  const current = loaded ? currentOptionOf(assignment, options, t_) : null
  // El valor actual puede NO ser una opción (la COPIA de la OF nunca se ofrece, RN20): va en la lista para
  // que el selector lo reconozca como valor, y se quita de lo que se pinta.
  const offeredIds = options.map((option) => option.id)
  const selectableValues = current && !offeredIds.includes(current.id) ? [...options, current] : options
  // RN18/RN29: TODOS los motivos, uno por línea, en el orden en que los manda Sun. ⚠️ Con el campo
  // bloqueado solo se explican los de `notEditableReasons`: los de `notClearableReasons` hablan de QUITAR
  // el workflow, y con el candado puesto no se puede ni cambiar (aviso de `contrato-sun.md` §7, OF no
  // editable y además replicada).
  const lockReasons = loaded && !editable ? assignment.notEditableReasons : []
  // EM-2 (S125): editable pero no se puede QUITAR (hoy solo `REPLICATED_IN_CLUSTER`): "Without workflow" no
  // se ofrece y el selector dice por qué. Cambiar a otro workflow sigue disponible.
  const clearBlockReasons = editable && !assignment.withoutWorkflow ? assignment.notClearableReasons : []
  // H187 (RN15-RN17, D-J3): the origin of the CURRENT copy, from the single formatter; with a pending choice it is
  // still the current one (the confirmation dialog is modal and cancelling restores the value). No workflow: empty.
  const originText = loaded ? workflowOriginTextOf(assignment.workflow.origin, t_) : ''
  const plannedKind = selection ? plannedKindOf(assignment, selection) : ''
  const confirmation = CONFIRMATION_TEXTS[plannedKind] || { title: '', body: '' }
  // Solo tiene sentido al cambiar o quitar: al asignar no hay ruta anterior (y C1 los trae a `false`).
  const routeDataWarning = plannedKind !== 'ASSIGN' && (assignment.workflowHasJobDocuments || assignment.workflowHasCamPrograms)

  // S110 (RN27, E4): otro usuario guardó la OF. De C1 solo se toma lo que decide si se puede seguir —la
  // marca, `editable` y los motivos—; mientras siga siendo editable se conserva la elección del usuario y
  // el siguiente C2 lleva la marca nueva. Si ya no lo es, la elección se descarta y queda el candado.
  const refreshAfterConcurrentChange = () => readAssignment()
    .then((fresh) => {
      if (!alive.current) return
      setAssignment((previous) => ({
        ...previous,
        lastModified: fresh.lastModified,
        editable: fresh.editable,
        notEditableReasons: fresh.notEditableReasons,
        notClearableReasons: fresh.notClearableReasons
      }))
      if (fresh.editable) {
        setConcurrentNotice(true)
      } else {
        setSelection(null)
        setConcurrentNotice(false)
      }
    })
    .catch(() => {
      setSelection(null)
      setConcurrentNotice(false)
    })

  // B3: cada rechazo, un aviso con la clave de su `code` (y E1 con sus motivos debajo).
  const handleRejection = (error) => {
    const rejection = rejectionOf(error)
    showAlert({
      severity: 'warning',
      title: t_(rejection.key, rejection.params),
      message: rejection.reasons.length > 0
        ? (
          <Box component='span' sx={{ display: 'block' }}>
            {rejection.reasons.map((reason) => (
              <Typography key={reason} data-testid='workflow-rejection-reason' variant='body2' component='span' sx={{ display: 'block' }}>
                {t_(reasonKeyOf(reason))}
              </Typography>
            ))}
          </Box>
          )
        : ''
    })
    if (rejection.concurrent) return refreshAfterConcurrentChange()
    setSelection(null)
    setConcurrentNotice(false)
    // Tras un 409 el campo toma lo que diga C1 (valor, candado, motivos); en los demás rechazos basta con
    // volver al valor anterior, que es el que ya tiene.
    if (rejection.reloadsAssignment) {
      return readAssignment().then((data) => { if (alive.current) setAssignment(data) }).catch(() => {})
    }
  }

  // C2 con la opción elegida y la marca que leyó la ficha (RN27). La centinela viaja como `null`
  // explícito: quitar el workflow (EM-1).
  // `confirming` is the synchronous guard: a second click before the repaint would send a second C2.
  const confirming = useRef(false)
  const confirm = () => {
    if (confirming.current) return Promise.resolve()
    confirming.current = true
    setSaving(true)
    return saveWorkflowAssignment(fabricationOrderId, isRemoval(selection) ? null : selection.id, assignment.lastModified)
      .then((result) => {
        setAssignment(result)
        setSelection(null)
        setConcurrentNotice(false)
        onAssigned(result)
      })
      .catch((error) => handleRejection(error))
      .finally(() => {
        confirming.current = false
        if (alive.current) setSaving(false)
      })
  }

  const choose = (option) => {
    if (!option || (current && option.id === current.id)) return
    setConcurrentNotice(false)
    setSelection(option)
  }

  // B6: cancelar no envía nada y el campo vuelve al valor que tenía antes de elegir.
  const cancel = () => {
    setSelection(null)
    setConcurrentNotice(false)
  }

  return {
    loaded,
    assignment,
    editable,
    offeredIds,
    selectableValues,
    value: selection || current,
    selection,
    lockReasons,
    clearBlockReasons,
    originText,
    confirmation,
    routeDataWarning,
    concurrentNotice,
    saving,
    choose,
    confirm,
    cancel
  }
}

export default useFabricationOrderWorkflow
